import {useStoreContext} from '@store/store-context';
import {FormEvent, memo, useState, useCallback, useEffect} from 'react';
import {useDebounce} from '@utils/debounce';
import {Body2} from '@ui/atoms/typography';
import {Button} from '@ui/atoms/button';
import {BillingType, useDropDown} from '@hooks';
import {AddMentions, AddSubscribers} from '@utils/firebase-request';
import {getFromBetween} from '@utils/get-between';
import {
  CalendarIcon,
  CancelIcon,
  CheckIcon,
  MoreVerticalIcon,
} from '@ui/atoms/icons';
import {ItemLoader} from '@ui/organisms/item-loader';
import styled from 'styled-components';
import {GoalsController} from '../../../../../../../api/goals-api/goals-controller';
import {GoalsRequestImpl} from '../../../../../../../api/goals-api/goals-request';
import {observer} from 'mobx-react-lite';
import {useFirebaseFetch} from '@hooks/query-hook';
import {useActionItems} from '@hooks/features-action-items';
import {Calendar} from '@ui/molecules/calendar';
import {Dropdown} from '@ui/molecules/dropdown';
import dayjs from 'dayjs';
import {generateRandomDigits} from '@utils/generate-random-numbers';
import {
  canDeleteGoalActionItem,
  canUpdateGoalActionItem,
} from '@utils/goal-permissions';
import {getColorFromTheme} from '@ui/ui-utils';

const ItemLoaderWrapper = styled.div`
  width: 100%;
  height: 100%;
  margin-top: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
`;
export const AdditionalOptionsWrapper = styled.div<{$display?: boolean}>`
  display: ${({$display}) => ($display ? 'block' : 'none')};
  background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='10' ry='10' stroke='rgba(213, 42, 42, 1)' strokeWidth='2' stroke-dasharray='14%2c 8' stroke-dashoffset='0' strokeLinecap='round'/%3e%3c/svg%3e");
  border-radius: 10px;
  margin: 8px 0;
  width: 100%;

  padding: 2px 2px;
  .wrapper {
    background: ${getColorFromTheme('red100')};
    width: 100%;
    display: flex;
    padding: 12px;
    height: 100%;
    justify-content: space-between;
    text-align: left;
    align-items: flex-start;
    .wrapper-div {
      padding-left: 24px;
      span {
        color: ${getColorFromTheme('red500')};
        font-weight: 600;
        text-decoration: underline;
        white-space: no-wrap;
      }
    }

    .cancel-div {
      padding-right: 24px;
    }
  }
`;

const Wrapper = styled.div`
  margin-top: 16px;
`;

const UpgradeLink = styled.span`
  color: ${getColorFromTheme('red500')};
  font-weight: 600;
  text-decoration: underline;
  white-space: nowrap;
`;

const CreateActionForm = styled.form`
  display: flex;
  align-items: stretch;
  gap: 10px;
`;

const ActionField = styled.div`
  display: flex;
  min-width: 0;
  min-height: 40px;
  flex: 1;
  align-items: center;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  transition: border-color 150ms ease, box-shadow 150ms ease;

  &:focus-within {
    border-color: ${getColorFromTheme('purple300')};
    box-shadow: 0 0 0 2px ${getColorFromTheme('purple200')};
  }
`;

const ActionInput = styled.input`
  min-width: 0;
  flex: 1;
  padding: 9px 12px;
  border: 0;
  outline: 0;
  background: transparent;
  color: ${getColorFromTheme('textDark')};
  font: 400 13px/20px Graphik, sans-serif;

  &::placeholder {
    color: ${getColorFromTheme('textBody')};
  }
`;

const AddActionButton = styled(Button)`
  min-width: 68px;
  min-height: 40px;
  padding: 0 18px;
  border-radius: 8px;
  font: 600 13px/20px Graphik, sans-serif;

  &:disabled {
    border-color: ${getColorFromTheme('backgroundDark')};
    background-color: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('textMuted')};
  }
`;

const DatePickerWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const DatePickerButton = styled.button<{$hasDate?: boolean}>`
  display: inline-flex;
  min-height: 30px;
  align-items: center;
  gap: 6px;
  margin-right: 6px;
  padding: 5px 8px;
  border: 0;
  border-radius: 6px;
  background: ${({$hasDate, theme}) =>
    $hasDate ? theme.colors.purple100 : 'transparent'};
  color: ${({$hasDate, theme}) =>
    $hasDate ? theme.colors.purple300 : theme.colors.textBody};
  font: 500 12px/18px Graphik, sans-serif;
  white-space: nowrap;

  svg path {
    stroke: currentColor;
  }

  &:hover:not(:disabled) {
    background: ${getColorFromTheme('backgroundDark')};
  }
`;

const CalendarPopover = styled.div`
  position: absolute;
  z-index: 20;
  top: calc(100% + 8px);
  right: 0;
`;

const ActionList = styled.div`
  display: grid;
  gap: 8px;
  margin-top: 10px;
`;

const ActionRow = styled.div`
  display: flex;
  min-height: 40px;
  align-items: center;
  gap: 9px;
  padding: 7px 8px 7px 11px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
`;

const CompleteButton = styled.button<{$completed?: boolean}>`
  display: inline-flex;
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1.5px solid
    ${({$completed, theme}) =>
      $completed ? theme.colors.purple300 : theme.colors.borderDark};
  border-radius: 4px;
  background: ${({$completed, theme}) =>
    $completed ? theme.colors.purple300 : theme.colors.white};

  svg {
    width: 12px;
    height: 12px;
    stroke: ${getColorFromTheme('alwaysWhite')};
    stroke-width: 2;
  }
`;

const ActionText = styled.span<{$completed?: boolean}>`
  min-width: 0;
  flex: 1;
  color: ${({$completed, theme}) =>
    $completed ? theme.colors.textBody : theme.colors.blackTwo};
  font: 400 13px/19px Graphik, sans-serif;
  text-decoration: ${({$completed}) => ($completed ? 'line-through' : 'none')};
  overflow-wrap: anywhere;
`;

const RowActions = styled.div`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 2px;
`;

interface DueDatePickerProps {
  value?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}

const DueDatePicker = memo(
  ({value = '', disabled, onChange}: DueDatePickerProps) => {
    const {handleOpen, ref, open, handleClose} = useDropDown();
    const label = value ? dayjs(value).format('D MMM') : 'Due date';

    return (
      <DatePickerWrapper ref={ref}>
        <DatePickerButton
          type="button"
          $hasDate={Boolean(value)}
          disabled={disabled}
          aria-label={
            value
              ? `Due ${dayjs(value).format('D MMM YYYY')}`
              : 'Select due date'
          }
          onClick={handleOpen as any}>
          <CalendarIcon />
          {label}
        </DatePickerButton>
        {open && (
          <CalendarPopover>
            <Calendar
              onChange={(date) => {
                onChange(dayjs(date).format());
                handleClose();
              }}
              value={value}
              disabledBefore={dayjs().subtract(1, 'day').format()}
            />
          </CalendarPopover>
        )}
      </DatePickerWrapper>
    );
  },
);

DueDatePicker.displayName = 'DueDatePicker';
export const useGoalTasksHook = (
  goalId: string,
  mentions: any[],
  defaultGoalSubscribers: any[],
  allGoalSubscribers: any[],
) => {
  const [loading, setLoading] = useState(true);
  const {
    authStore: {auth},
    usersStore: {users},
  } = useStoreContext();

  const goalPath = `action-items/${process.env.REACT_APP_DEV_ENV}/${goalId}/tasks`;

  const {data: goalFirebaseTasks} = useFirebaseFetch(goalPath);

  const [tasks, setTasks] = useState([
    {
      text: '',
      completed: false,
    },
  ]);

  const fetchActivities = useCallback(async () => {
    const request = new GoalsRequestImpl();
    const controller = new GoalsController(request);

    const data = await controller.fetchGoalTasksById(goalId);

    if (data && data?.tasks.length > 0) {
      const computeTasks = data.tasks.map((task: {_id: string}) => {
        return {
          ...task,
          user: goalFirebaseTasks?.[task?._id]
            ? users.find(
                (user) => user.id === goalFirebaseTasks?.[task?._id]?.user,
              )
            : undefined,
        };
      });

      setTasks(computeTasks);
    }

    setLoading(false);
  }, [goalFirebaseTasks, goalId, users]);

  useEffect(() => {
    if (goalId) {
      fetchActivities();
    }
  }, [fetchActivities, goalId]);

  const [getMentions] = useState(mentions);
  const [getGoalSubscribers] = useState(allGoalSubscribers);
  const [getdefaultGoalSubscribers] = useState(defaultGoalSubscribers);

  const updateTasks = useCallback(
    async (data: {
      tasks?: {text?: string; completed?: boolean}[];
      goalId: string;
    }) => {
      const getTasksMentions: any = [];

      const request = new GoalsRequestImpl();
      const controller = new GoalsController(request);

      const response = await controller.updateGoalTasksById(data.goalId, {
        tasks: data.tasks,
      });

      if (response) {
        const computeTasks = response.tasks.map((task: {_id: string}) => {
          return {
            ...task,
            user: goalFirebaseTasks?.[task?._id]
              ? users.find(
                  (user) => user.id === goalFirebaseTasks?.[task?._id]?.user,
                )
              : undefined,
          };
        });

        setTasks(computeTasks);
      }

      data.tasks?.forEach((task) => {
        const mentionedUsers = getFromBetween.get(task.text, '_(', ')_');

        if (mentionedUsers.length > 0) getTasksMentions.push(...mentionedUsers);
      });

      const handleNewAddMentions = (data: any) => {
        data.forEach((id: string) => {
          AddMentions(
            id,
            goalId,
            {
              unreadTasks: getTasksMentions.includes(id) ? 1 : 0,
              tasksMentionedBy: getTasksMentions.includes(id)
                ? [auth.user.id]
                : [],
              mentionedBy: [],
              unreadComment: 0,
            },
            'goals',
          );
        });
      };
      if (getGoalSubscribers.length > 0) {
        const users = [
          ...new Set([...getGoalSubscribers, ...getTasksMentions]),
        ];
        AddSubscribers(goalId, users);
        if (getMentions.length > 0) {
          getMentions.forEach((user: any) => {
            const id: any = Object.keys(user)[0];
            const tasksMentionedBy = user[id]?.tasksMentionedBy || [];

            AddMentions(
              id,
              goalId,
              {
                unreadTasks:
                  user[id]?.unreadTasks && getTasksMentions.includes(id)
                    ? user[id]?.unreadTasks + 1
                    : getTasksMentions.includes(id)
                    ? 1
                    : 0,
                tasksMentionedBy: getTasksMentions.includes(id)
                  ? [...new Set([...tasksMentionedBy, auth.user.id])]
                  : [...new Set([...tasksMentionedBy])],
                mentionedBy: user[id]?.mentionedBy || [],
                unreadComment: user[id]?.unreadComment || 0,
              },
              'goals',
            );
          });
        } else {
          handleNewAddMentions(users);
        }
      } else {
        const users = [
          ...new Set([...getdefaultGoalSubscribers, ...getTasksMentions]),
        ];
        AddSubscribers(goalId, users);
        handleNewAddMentions(users);
      }
    },
    [
      getGoalSubscribers,
      goalFirebaseTasks,
      users,
      goalId,
      auth.user.id,
      getMentions,
      getdefaultGoalSubscribers,
    ],
  );

  const debounceUpdateTasks = useDebounce(updateTasks, 1000);

  return {
    loading,
    debounceUpdateTasks,
    updateTasks,
    setTasks,
    tasks,
  };
};

export const GoalTasks = observer(
  ({
    goalId,
    actionItems,
    isReadOnly,
    currentUserMentions,
  }: {
    goalId: string;
    actionItems: ReturnType<typeof useActionItems>;
    unreadTasks: number;
    isReadOnly?: boolean;
    currentUserMentions: any;
    userId: string;
    mentions: any[];
    defaultGoalSubscribers: any[];
    allGoalSubscribers: any[];
  }) => {
    const {
      authStore: {auth},
      usersStore: {users},
    } = useStoreContext();

    const {handleChange, isLoading, tasks} = actionItems;

    const [showPaidPlanModal, setShowPaidPlanModal] = useState(true);
    const [newAction, setNewAction] = useState('');
    const [newDueDate, setNewDueDate] = useState('');
    const [isAdding, setIsAdding] = useState(false);

    useEffect(() => {
      if (Number(currentUserMentions?.unreadTasks || 0) > 0) {
        AddMentions(
          auth.user.id,
          goalId,
          {
            ...currentUserMentions,
            unreadTasks: 0,
            tasksMentionedBy: [],
          },
          'goals',
        );
      }
    }, [auth.user.id, currentUserMentions, goalId]);

    const createActionItem = async (event: FormEvent) => {
      event.preventDefault();
      const text = newAction.trim();
      if (!text || isAdding || isReadOnly) return;

      const key = generateRandomDigits(24);
      const item = {
        assignee: auth.user.id,
        completed: false,
        dueDate: newDueDate || undefined,
        id: key,
        key,
        order: tasks.length + 1,
        source: 'goal',
        sourceId: goalId,
        text,
        updatedBy: auth.user.id,
      };

      setIsAdding(true);
      await handleChange([item], '', key, item);
      setNewAction('');
      setNewDueDate('');
      setIsAdding(false);
    };

    const updateTask = async (task: any, updates: Record<string, unknown>) => {
      if (isReadOnly || !canUpdateGoalActionItem(task, auth.user.id, users)) {
        return;
      }
      const updatedTask = {...task, ...updates};
      await handleChange(
        [updatedTask],
        updates.dueDate !== undefined ? 'dueDate' : '',
        task.key,
        updatedTask,
      );
    };

    const canDeleteTask = (task: any) => {
      if (isReadOnly) return false;
      return canDeleteGoalActionItem(task, auth.user, users);
    };

    if (isLoading) {
      return (
        <ItemLoaderWrapper>
          <ItemLoader />
        </ItemLoaderWrapper>
      );
    }

    return (
      <Wrapper>
        <AdditionalOptionsWrapper
          $display={
            showPaidPlanModal &&
            auth.user.workspace.billing.plan === BillingType.FREE
          }>
          <div className="wrapper">
            <div className="wrapper-div">
              <Body2>
                This feature isn’t available for your current account plan, to
                access this feature{' '}
                <UpgradeLink> upgrade your account</UpgradeLink>
              </Body2>
            </div>
            <div
              onClick={() => setShowPaidPlanModal(false)}
              className="cancel-div">
              <CancelIcon />
            </div>
          </div>
        </AdditionalOptionsWrapper>

        {!isReadOnly && (
          <CreateActionForm onSubmit={createActionItem}>
            <ActionField>
              <ActionInput
                aria-label="Action item"
                value={newAction}
                placeholder="Add an action item"
                onChange={(event) => setNewAction(event.target.value)}
              />
              <DueDatePicker value={newDueDate} onChange={setNewDueDate} />
            </ActionField>
            <AddActionButton
              type="submit"
              disabled={!newAction.trim() || isAdding}>
              {isAdding ? 'Adding…' : 'Add'}
            </AddActionButton>
          </CreateActionForm>
        )}

        {tasks.length > 0 && (
          <ActionList aria-label="Action items">
            {tasks.map((task: any) => {
              const canUpdateTask =
                !isReadOnly &&
                canUpdateGoalActionItem(task, auth.user.id, users);
              return (
                <ActionRow key={task.key}>
                  <CompleteButton
                    type="button"
                    $completed={task.completed}
                    disabled={!canUpdateTask}
                    aria-label={
                      task.completed
                        ? `Mark ${task.text} as incomplete`
                        : `Mark ${task.text} as complete`
                    }
                    onClick={() =>
                      updateTask(task, {completed: !task.completed})
                    }>
                    {task.completed && <CheckIcon />}
                  </CompleteButton>
                  <ActionText $completed={task.completed}>
                    {task.text}
                  </ActionText>
                  <RowActions>
                    <DueDatePicker
                      value={task.dueDate || ''}
                      disabled={!canUpdateTask}
                      onChange={(dueDate) => updateTask(task, {dueDate})}
                    />
                    {canDeleteTask(task) && (
                      <Dropdown
                        customIcon={<MoreVerticalIcon />}
                        menu={[
                          ...(task.dueDate
                            ? [
                                {
                                  content: 'Remove due date',
                                  id: 'removeDueDate',
                                  icon: null,
                                },
                              ]
                            : []),
                          {content: 'Delete', id: 'delete', icon: null},
                        ]}
                        onClick={(action) => {
                          if (action === 'removeDueDate') {
                            void updateTask(task, {dueDate: ''});
                          }
                          if (action === 'delete') {
                            void handleChange([], 'remove', task.key, task);
                          }
                        }}
                      />
                    )}
                  </RowActions>
                </ActionRow>
              );
            })}
          </ActionList>
        )}
      </Wrapper>
    );
  },
);
